<template>
  <div id="artwork-history" ref="modal" class="modal artwork-history" role="dialog">
    <div class="artwork-history__modal-box modal-dialog">
      <div
        class="artwork-history__modal-header"
        role="heading"
        aria-label="Your previous purchases"
        tabIndex="0"
      >
        <span class="artwork-history__title"> My Library </span>
        <button
          class="artwork-history__modal-button-close"
          type="button"
          @click="onClose('close_button')"
        >
          <UseSvg icon="modal-close-big" />
        </button>
      </div>
      <div class="artwork-history__modal-body">
        <div class="artwork-history__subheader">
          <div v-if="!isSearchExpanded" class="artwork-history__dropdown" @click="toggleSelect">
            <span class="artwork-history__field-adornment">
              <UseSvg
                class="artwork-history__field-adornment-icon"
                :icon="`arrow-${sortOrder === 'asc' ? 'up' : 'down'}`"
              />
            </span>
            <select
              ref="sortOrderSelect"
              v-model="sortOrder"
              class="artwork-history__dropdown-select"
            >
              <option value="desc">Newest First</option>
              <option value="asc">Oldest First</option>
            </select>
          </div>

          <ArtworkHistoryArtworksToggle
            v-if="reorderEnabled && !isSearchExpanded"
            class="artwork-history__toggle"
            :disabled="apiLoading"
            :type="artworksType"
            @update-type="updateArtworksType"
          />

          <div
            class="artwork-history__input"
            :class="{ 'artwork-history__input--full-width': isSearchExpanded }"
          >
            <input
              v-if="isSearchExpanded || !isMobile"
              v-model="search"
              class="artwork-history__input-field"
              placeholder="Search image names"
              type="text"
              @input="onSearch"
            />
            <span class="artwork-history__field-adornment" @click="onToggleSearch">
              <UseSvg class="artwork-history__field-adornment-icon" icon="search" />
            </span>
          </div>

          <button
            v-if="isSearchExpanded"
            class="artwork-history__modal-button-close"
            type="button"
            @click="onToggleSearch"
          >
            <UseSvg icon="modal-close-big" />
          </button>
        </div>
        <ArtworkHistoryModalEmptyState
          v-if="artworkHistory.length < 1"
          :section-type="currentType"
          :is-uploaded-state="reorderEnabled && artworksType === 'uploaded'"
          :is-loading="apiLoading"
          :is-made="isMade"
        />
        <div v-else class="artwork-history__artworks-wrapper" @scroll="scroll">
          <ArtworkHistoryNotification
            v-if="reorderEnabled && artworksType === 'purchased'"
            class="artwork-history__artworks-notification"
          >
            You won't be charged for design services when reordering a design at the same size.
          </ArtworkHistoryNotification>

          <div class="artwork-history__grid">
            <template v-if="isLibrarySection">
              <ArtworkHistoryGridItem
                v-for="(asset, index) in formattedLibraryItems"
                :key="index"
                :artwork="asset"
                :is-library="true"
                :is-selected="isLibrarySelected(asset)"
                :index="index"
                :is-shown-tier="false"
                :is-shown-checkbox="true"
                @select-library="selectLibrary"
              />
            </template>
            <template v-else>
              <ArtworkHistoryGridItem
                v-for="(artwork, index) in formattedArtworks"
                :key="index"
                :artwork="artwork"
                :is-library="false"
                :is-selected="isArtworkSelected(artwork)"
                :index="index"
                :is-shown-tier="reorderEnabled && artworksType === 'purchased'"
                :is-shown-checkbox="!isMaxNumberOfArtworksSelected || isArtworkSelected(artwork)"
                @download-artwork="download"
                @select-library="selectArtwork"
              />
            </template>
            <div
              class="artwork-history__loading"
              :class="{ 'artwork-history__loading--visible': isLazyLoading }"
            >
              <Loader :class="'artwork-history__loading-icon'" />
            </div>
          </div>
        </div>
      </div>
      <ArtworkHistoryModalFooter
        v-if="selectedArtworks.length > 0"
        :count="selectedArtworkCountMessage"
        :cta-text="buttonText"
        :is-loading-cta="isLoadingCta"
        @close="onClose"
        @cta-action="onSubmit"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useI18n } from "vue-i18n";
import { api } from "jiffy-api";
import debounce from "lodash.debounce";
import { pluralize } from "common/utils/format/word";
import { isMobile } from "common/utils/browser";
import { writeCookie } from "common/utils/cookie";
import Loader from "common/vue/components/Loader.vue";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { track } from "jiffy-analytics";
import UseSvg from "../vue-cart/UseSvg.vue";
import ArtworkHistoryModalEmptyState from "./ArtworkHistoryModalEmptyState.vue";
import ArtworkHistoryGridItem from "./ArtworkHistoryGridItem.vue";
import ArtworkHistoryModalFooter from "./ArtworkHistoryModalFooter.vue";
import { isArtworkWithError } from "./utils";
import { useDtfStore } from "./store";
import { useSpanglesStore } from "./spangles-module";
import ArtworkHistoryArtworksToggle from "./ArtworkHistoryArtworksToggle.vue";
import ArtworkHistoryNotification from "./ArtworkHistoryNotification.vue";

export default {
  name: "ArtworkHistoryModal",
  components: {
    ArtworkHistoryNotification,
    ArtworkHistoryArtworksToggle,
    ArtworkHistoryModalFooter,
    ArtworkHistoryGridItem,
    ArtworkHistoryModalEmptyState,
    UseSvg,
    Loader
  },
  props: {
    index: {
      type: Number,
      required: false,
      default: 0
    },
    singleSelectionMode: {
      type: Boolean,
      required: false,
      default: false
    },
    reorderEnabled: {
      type: Boolean,
      required: false,
      default: false
    },
    isMade: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      isSearchExpanded: false,
      isLoadingCta: false,
      sortOrder: "desc",
      artworksType: "uploaded",
      currentType: "true",
      search: "",
      selectedArtworks: [],
      apiLoading: false,
      isLazyLoading: false,
      isMobile: isMobile()
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "artworkHistory",
      "artworkHistoryPagination",
      "catalogNumber",
      "pdpType",
      "sticker",
      "isPuff",
      "isSpangle",
      "isStickerSample",
      "stickerQrScanned",
      "artworks",
      "isGoldFoil",
      "puffMaxImageCount",
      "printAsIs",
      "businessLine"
    ]),
    ...mapState(useSpanglesStore, ["spanglesMaxImageCount"]),
    formattedLibraryItems() {
      return this.artworkHistory.map(
        ({
          id = null,
          title = "",
          previewImagesUrls = [],
          price = "",
          originalPrice = "",
          author = "",
          slug = ""
        }) => ({
          id,
          price,
          author,
          originalPrice,
          name: title,
          slug,
          previewUrl: previewImagesUrls[0]
        })
      );
    },
    /* eslint-disable max-lines-per-function */
    formattedArtworks() {
      return this.artworkHistory.map(
        ({
          id = null,
          fileName = "",
          originalMime = "",
          enhancementTier = "",
          uploadedOn = "",
          purchasedDate = "",
          ordered = {},
          original = {},
          useFootLength,
          dtfPrintId
        }) => ({
          id,
          dtfPrintId,
          name: fileName,
          uploadedOn,
          purchasedDate,
          originalMime,
          enhancementTier,
          url: ordered.imageUrl,
          previewUrl: this.getPreviewUrl(original, ordered),
          downloadUrl: this.getDownloadUrl(original, ordered),
          spangled: this.getSpangledState(ordered),
          spangledImageUrl: ordered?.spangledImageUrl,
          cutlineUrl: ordered?.cutlineUrl,
          originalImageUrl: original.imageUrl,
          originalMimeType: original.mimeType,
          cmykPreviewImageNoBackgroundUrl: ordered?.cmykPreviewImageNoBackgroundUrl,
          cmykPreviewImageWithBackgroundUrl: ordered?.cmykPreviewImageWithBackgroundUrl,
          processTimeStamp: Date.now(),
          goildFoilPreviewAttached: this.isGoldFoil && this.currentType === "true",
          enhancedNoBgUrl: ordered.noBgUrl,
          enhancedWithBgUrl: ordered.withBgUrl,
          enhancementCompleted: true,
          originalDpi: ordered.dpi,
          enhancedDpi: ordered.dpi,
          sliderPreviewUrl: ordered.noBgNoTrimUrl,
          canRemoveBg: ordered.canRemoveBg || false,
          useFootLength: useFootLength || false,
          metadata: {
            ...ordered.metadata,
            dpi: ordered.dpi,
            width: ordered.widthPx,
            height: ordered.heightPx,
            widthInch: ordered.width,
            heightInch: ordered.height,
            maxSheetHeight: ordered.maxSheetHeight,
            maxSheetWidth: ordered.maxSheetWidth,
            aspectRatio: ordered?.enhanceImage
              ? (
                  parseFloat(ordered?.enhancedMetadata?.width) /
                  parseFloat(ordered?.enhancedMetadata?.height)
                ).toPrecision(6)
              : (
                  parseFloat(ordered?.originalMetadata?.width) /
                  parseFloat(ordered?.originalMetadata?.height)
                ).toPrecision(6),
            lengthFoot: ordered.lengthFoot,
            removeBackground: ordered.removeBackground
          },
          originalMetadata: ordered?.originalMetadata,
          enhancedMetadata: ordered?.enhancedMetadata,
          withBgMetadata: ordered?.withBgMetadata,
          enhanceImage: ordered?.enhanceImage,
          previewMetadata: this.printAsIs ? { heightInch: ordered.height } : {},
          enhancedOriginalDpi: ordered?.enhancedOriginalDpi,
          originalWidth: this.defineOriginalWidth(ordered),
          originalHeight: this.defineOriginalHeight(ordered),
          sizeBasedDpi: ordered?.enhanceImage
            ? ordered?.enhancedOriginalDpi
            : ordered?.originalMetadata?.dpi
        })
      );
    },
    /* eslint-enable max-lines-per-function */
    selectedArtworkCountMessage() {
      if (this.isLibrarySection) return "Maximum 1 design can be selected."; // Always one for Library
      return this.isMaxNumberOfArtworksSelected
        ? `Maximum ${this.t("image", this.selectedArtworks.length)} can be selected.`
        : `${this.selectedArtworks.length} Selected`;
    },
    isMaxNumberOfArtworksSelected() {
      return (
        (this.isPuff && this.selectedArtworks.length === this.puffMaxImageCount) ||
        (this.isSpangle && this.selectedArtworks.length === this.spanglesMaxImageCount) ||
        (this.isStickerSample && this.selectedArtworks.length >= 1)
      );
    },
    buttonText() {
      if (this.isSpangle && this.currentType === "false") {
        return "Spangle It";
      }

      if (this.reorderEnabled && this.artworksType === "purchased") {
        return "Add to Cart";
      }

      return `Select ${pluralize(this.selectedArtworks.length, "Design", "s", true)}`;
    },
    isLibrarySection() {
      return this.currentType === "library";
    },
    withError() {
      return isArtworkWithError(this.artworks[0]);
    },
    isPdp() {
      return this.pdpType !== "non-pdp";
    }
  },
  watch: {
    sortOrder() {
      this.setArtworkHistoryPagination({ page: 1 });
      this.clearSelection();
      this.loadArtworkHistory();
    },
    currentType() {
      this.setArtworkHistoryPagination({ page: 1 });
      this.setArtworkHistory([]);
      this.clearSelection();
      this.loadArtworkHistory();
      track("modal_with_previous_uploads_tab_selected", { current_type: this.currentType });
    },
    search(newValue, oldValue) {
      if (!newValue && oldValue) {
        this.apiLoading = true;
      }
    }
  },
  beforeMount() {
    this.onSearch = debounce(this.onSearch, 600);
    this.currentType = "true";

    if (this.stickerQrScanned) {
      this.currentType = "false";
    }
  },
  mounted() {
    this.onShow();
  },
  beforeUnmount() {
    $(this.$refs.modal).modal("hide");
    this.setShowArtworkHistoryModal(false);
  },
  methods: {
    ...mapActions(useDtfStore, [
      "setShowArtworkHistoryModal",
      "setArtworkHistory",
      "setArtworkHistoryPagination",
      "removeArtwork"
    ]),
    ...mapActions(useSpanglesStore, ["setSpangleVariations"]),
    ...mapActions(useDtfStore, [
      "getArtworkHistory",
      "submitPrintReorder",
      "getLibraryCollection",
      "buyArtworksAgain",
      "downloadAndTriggerArtworkUploadFlow"
    ]),
    selectArtworkHistory(type) {
      this.currentType = type;
    },
    async updateArtworksType(type) {
      this.artworksType = type;
      this.setArtworkHistoryPagination({ page: 1 });
      this.clearSelection();
      this.setArtworkHistory([]);
      await this.loadArtworkHistory();
    },
    async onShow() {
      this.showModal();
      this.setArtworkHistoryPagination({
        perPage: this.isMobile ? 20 : 36
      });
      this.loadArtworkHistory();
    },
    onClose(source) {
      if (source) {
        this.sendArtworkHistoryEvent("previous_purchase_modal_closed", { source });
      }
      this.setShowArtworkHistoryModal(false);
      this.setArtworkHistory([]);
      this.setArtworkHistoryPagination({ page: 1 });
      $(this.$refs.modal).modal("hide");
    },
    sendArtworkHistoryEvent(event, data) {
      submitDtfEvent(event, {
        page: "pdp",
        business_line: this.businessLine,
        pdp_type: this.pdpType,
        catalog_number: this.catalogNumber,
        ...data
      });
    },
    showModal() {
      const $modal = $(this.$refs.modal);
      $modal.modal("show").on("hidden.bs.modal", () => {
        this.onClose();
        document.body.classList.remove("dtf-artwork-history-open");
      });

      document.body.classList.add("dtf-artwork-history-open");
    },
    defineOriginalWidth(ordered) {
      if (ordered.enhanceImage) {
        return ordered.removeBackground
          ? ordered.enhancedMetadata?.widthInch
          : ordered.withBgMetadata?.widthInch;
      }

      return ordered.originalMetadata?.widthInch;
    },
    defineOriginalHeight(ordered) {
      if (ordered.enhanceImage) {
        return ordered.removeBackground
          ? ordered.enhancedMetadata?.heightInch
          : ordered.withBgMetadata?.heightInch;
      }

      return ordered.originalMetadata?.heightInch;
    },
    getPreviewUrl(original, ordered) {
      if (this.isGoldFoil && this.currentType === "true") return ordered.goldFoilImage;

      const imageUrl = this.getDownloadUrl(original, ordered);

      return `${imageUrl}&w=150&q=70&dpr=2`;
    },
    getDownloadUrl(original, ordered) {
      if (this.currentType === "false") {
        return original.imageUrl;
      }
      if (this.isSpangle && ordered.spangledImageUrl) {
        return ordered.spangledImageUrl;
      }
      if (this.sticker && ordered.cuttingUrl) {
        return ordered.cutlineUrl;
      }
      return original.imageUrl;
    },
    getSpangledState(ordered) {
      return this.isSpangle && ordered.spangled && this.currentType === "true";
    },
    async useDesign() {
      this.isLoadingCta = true; // Prevent double click

      const [asset] = this.selectedArtworks;
      const fileIds = await api.digitalAssets.getUploadFileIds({ slug: asset.slug });
      const uploadPromises = fileIds.map(id => {
        const formData = new FormData();
        formData.append("catalog_number", this.catalogNumber);
        formData.append("digital_assets_file_id", id);
        return api.dtf.uploadArtwork(formData, {
          raw: true,
          headers: { "Content-Type": undefined },
          useAxiosForRequest: true
        });
      });

      Promise.all(uploadPromises).then(results => {
        const artworkIds = results.map(artwork => artwork.id);
        writeCookie("uploaded_artwork_ids", JSON.stringify(artworkIds), 300);

        this.sendArtworkHistoryEvent("library_use_design_clicked", {
          artworks_ids: [asset.id],
          artworks_count: 1
        });

        this.onClose("buy_again_button");

        window.location.reload();
      });
    },
    async buyPrintReorder() {
      this.isLoadingCta = true; // Prevent double click
      await this.submitPrintReorder(this.selectedArtworks);
      this.onClose("buy_again_button");
      this.isLoadingCta = false;
    },
    async buyAgain() {
      this.setArtworkHistoryPagination({ page: 1 });
      await this.fetchSelectedArtworks();
      this.selectedArtworks = this.formattedArtworks;

      if (this.withError) {
        this.removeArtwork(this.artworks[0].id);
      }
      if (this.isSpangle) this.setSpangleVariations([]);
      if (this.sticker) {
        this.downloadAndTriggerArtworkUploadFlow(this.selectedArtworks);
      } else if (this.isPdp) {
        this.buyArtworksAgain(this.selectedArtworks, this.index);
      }
      this.sendArtworkHistoryEvent("previous_purchase_buy_again_clicked", {
        artworks_ids: this.selectedArtworks.map(artwork => artwork.id),
        artworks_count: this.selectedArtworks.length
      });
      this.onClose("buy_again_button");
    },
    clearLoading() {
      this.apiLoading = false;
      this.isLazyLoading = false;
    },
    async fetchSelectedArtworks() {
      this.apiLoading = true;
      const props = {
        search: this.search,
        sortOrder: this.sortOrder,
        artworkIds: this.selectedArtworks.map(artwork => artwork.id),
        currentType: this.currentType,
        apiType: "purchased"
      };

      if (this.isLibrarySection) {
        await this.getLibraryCollection(props);
      } else {
        await this.getArtworkHistory(props);
      }

      this.clearLoading();
    },
    async loadArtworkHistory() {
      this.apiLoading = true;
      const props = {
        artworksType: this.artworksType,
        search: this.search,
        sortOrder: this.sortOrder,
        artworkIds: [],
        currentType: this.currentType,
        apiType: "list"
      };

      if (this.isLibrarySection) {
        await this.getLibraryCollection(props);
      } else {
        await this.getArtworkHistory(props);
      }

      this.clearLoading();
    },
    onToggleSearch() {
      if (this.isMobile) {
        this.isSearchExpanded = !this.isSearchExpanded;
      }
    },
    clearSelection() {
      this.selectedArtworks = [];
    },
    checkArtworkSelection({ id, metadata }, artwork) {
      const selectedArtworkSize = `${metadata.widthInch}${metadata.heightInch}`;
      const artworkSize = `${artwork.metadata.widthInch}${artwork.metadata.heightInch}`;
      return id === artwork.id && selectedArtworkSize === artworkSize;
    },
    isArtworkSelected(artwork) {
      return this.selectedArtworks.some(selectedArtwork =>
        this.checkArtworkSelection(selectedArtwork, artwork)
      );
    },
    isLibrarySelected(artwork) {
      return this.selectedArtworks.some(selectedArtwork => selectedArtwork.id === artwork.id);
    },
    selectArtwork(artwork, index) {
      if (this.singleSelectionMode) {
        this.selectedArtworks = [this.formattedArtworks[index]];
        return;
      }

      if (this.isArtworkSelected(artwork)) {
        this.selectedArtworks = this.selectedArtworks.filter(
          selectedArtwork => !this.checkArtworkSelection(selectedArtwork, artwork)
        );
      } else {
        this.selectedArtworks = [...this.selectedArtworks, this.formattedArtworks[index]];
      }
    },
    selectLibrary(library, index) {
      this.selectedArtworks = [this.formattedLibraryItems[index]];
    },
    scroll({ target }) {
      const { page, totalPages } = this.artworkHistoryPagination;
      const isBottom = Math.ceil(target.scrollTop) >= target.scrollHeight - target.offsetHeight;
      if (!isBottom || page >= totalPages) {
        return;
      }
      this.isLazyLoading = true;
      this.setArtworkHistoryPagination({ page: page + 1 });
      this.loadArtworkHistory();
      this.sendArtworkHistoryEvent("previous_purchase_lazy_loaded", { page_number: page + 1 });
    },
    onSearch() {
      this.sendArtworkHistoryEvent("previous_purchase_search_triggered", {
        search_query: this.search
      });
      this.setArtworkHistoryPagination({ page: 1 });
      this.clearSelection();
      this.loadArtworkHistory();
    },
    download(url, name) {
      fetch(url)
        .then(response => response.blob())
        .then(blob => {
          const $a = document.createElement("a");
          $a.href = URL.createObjectURL(blob);
          $a.download = name;
          document.body.appendChild($a);
          $a.click();
          document.body.removeChild($a);
          this.sendArtworkHistoryEvent("previous_purchase_image_downloaded", {
            file_name: name,
            image_url: url,
            file_size: blob.size,
            file_type: blob.type
          });
        });
    },
    toggleSelect() {
      if (window.getComputedStyle(this.$refs.sortOrderSelect).display === "none") {
        if (this.sortOrder === "asc") {
          this.sortOrder = "desc";
        } else {
          this.sortOrder = "asc";
        }
      }
    },
    onSubmit() {
      if (this.isLibrarySection) {
        this.useDesign();
      } else if (this.reorderEnabled && this.artworksType === "purchased") {
        this.buyPrintReorder();
      } else {
        this.buyAgain();
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";
@import "frontend/init/jiffy/modal.scss";

.artwork-history {
  @include crafterpay-modal;

  overflow-x: hidden;
  overflow-y: auto;
  font-family: $digital-assets-font-family;

  &.modal {
    z-index: 9999;
  }

  &__modal-box {
    height: 100%;

    @include with-tablet {
      height: unset;
      max-width: 815px;
    }

    @include with-desktop {
      max-width: 1080px;
      margin: 10px auto;
    }
  }

  &__modal-header {
    padding: 0 15px;
    justify-content: space-between;
    border-bottom: $crafterpay-gray-modern-200 1px solid;

    @include with-desktop {
      padding: 12px 15px;
      height: 55px;
      padding: 20px;
    }
  }

  &__title {
    font-family: inherit;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: $gray-modern-700;
  }

  &__modal-body {
    display: flex;
    flex-direction: column;
  }

  &__subheader {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    padding-bottom: 24px;
    gap: 16px;
  }

  &__modal-button-close {
    position: inherit;
    flex: none;
    padding: 12px;
    margin: -12px;
    width: 40px;
    height: 40px;
    color: $crafterpay-gray-modern-400;
  }

  &__dropdown {
    display: flex;
    align-items: center;
    border: 1px solid $crafterpay-gray-modern-400;
    border-radius: 8px;
    padding: 0 12px;
    font-family: inherit;
    font-size: 16px;
    font-weight: 600;
    height: 42px;

    @include with-desktop {
      width: 235px;
    }
  }

  &__dropdown-select {
    width: 100%;
    height: 40px;
    padding: 10px 12px 12px 5px;
    border: 0;
    margin: 0;
    outline: 0;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    display: none;

    @include with-tablet {
      display: block;
    }
  }

  &__field-adornment {
    font-size: 14px;
    border-right: 0;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    display: flex;
    align-items: center;
  }

  &__field-adornment-icon {
    width: 16px;
    height: 16px;
    color: $crafterpay-gray-modern-600;
  }

  &__input {
    display: flex;
    align-items: center;
    border: 1px solid $crafterpay-gray-modern-300;
    border-radius: 8px;
    padding: 0 12px;
    font-family: inherit;
    font-size: 16px;
    font-weight: 600;
    height: 42px;
    background-color: $crafterpay-gray-modern-50;

    @include with-desktop {
      width: 300px;
    }

    &--full-width {
      width: 100%;
      margin-left: 0;
    }
  }

  &__input-field {
    width: 100%;
    height: 40px;
    padding: 12px 12px 12px 0px;
    border: 0;
    margin: 0;
    outline: 0;
    font-family: inherit;
    font-size: inherit;
    background-color: $crafterpay-gray-modern-50;

    &::placeholder {
      color: $crafterpay-gray-modern-500;
      font-weight: 400;
    }
  }

  &__artworks-wrapper {
    padding-bottom: 16px;
    overflow-x: hidden;
    max-height: 768px;
    overflow-y: auto;

    @include with-tablet {
      height: calc(100vh - 430px);
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(160px, 1fr));
    gap: 5px;

    @include with-large-2-mobile {
      grid-template-columns: repeat(2, minmax(175px, 1fr));
    }

    @include with-tablet {
      grid-template-columns: repeat(3, minmax(230px, 1fr));
      gap: 30px;
    }

    @include with-desktop {
      grid-template-columns: repeat(4, minmax(230px, 1fr));
    }
  }

  &__loading {
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    grid-column: 1 / span 2;
    visibility: hidden;

    &--visible {
      visibility: visible;
    }

    @include with-tablet {
      grid-column: 1 / span 3;
    }

    @include with-desktop {
      grid-column: 1 / span 4;
    }
  }

  &__loading-icon {
    width: 40px;
    height: 40px;
    color: $crafterpay-gray-modern-300;
  }

  &__toggle {
    margin: 0 auto;
  }

  &__artworks-notification {
    margin-bottom: 16px;

    @include with-tablet {
      margin-bottom: 24px;
    }
  }
}
</style>
